import { ActionFunctionArgs, LoaderFunction, json } from "@remix-run/node";
import {
  Link,
  MetaFunction,
  useLoaderData,
  useLocation,
} from "@remix-run/react";
import { dataJasaBangunRumahByDistrictBekasi } from "~/api/data-jasa-bangun-rumah-by-district-bekasi-by-services/dataJasaBangunRumahByDistrictBekasi";
import { useEffect, useState } from "react";
import {
  InterfaceBreadcrumbItem,
  InterfaceGlobalServices,
  InterfaceJasaByDistrict,
  InterfaceJasaByDistrictService,
} from "~/types/GlobalInterfaces";
import {
  ADDRESS,
  ADDRESS_CITY,
  AUTHOR,
  BRAND,
  dateModified,
  datePublished,
  DESCRIPTION_PROD_SCHEME,
  LOGO,
  OG_IMAGE,
  POSTAL_CODE,
  priceValidUntil,
  PROFILE_FACEBOOK,
  PROFILE_IMAGE,
  PROFILE_INSTAGRAM,
  PROFILE_LINKEDIN,
  SITE_URL,
  SLUG_JASA_BANGUN_RUMAH_DI_BEKASI,
  SPAM_WORD_1,
  TWITTER_IMAGE,
  WHATSAPP_MESSAGE,
  WHATSAPP_NUM,
} from "~/assets/profile/profile";
import { parseWithZod } from "@conform-to/zod";
import { GlobalFormSchema } from "~/scheme/global_form_schema";
import GlobalMetaScripts from "~/components/global/GlobalMetaScripts";
import GlobalBreadcrumb from "~/components/global/GlobalBreadcrumb";
import { HomeSectionHero } from "../home/components/HomeSectionHero";
import { GlobalRating } from "~/components/global/GlobalRating";
import Logo from "~/assets/global/logo";
import GlobalContactWhatsappFloating from "~/components/global/GlobalContactWhatsappFloating";
import ExploreJasaByCitySection from "~/components/global/ExploreJasaByCitySection";
import GlobalCompoVideo from "~/components/global/GlobalCompoVideo";
import { titleWithOptionalBranding, truncatedDesc } from "~/lib/utils";
import GlobalSectionHero from "~/components/global/GlobalSectionHero";
import { Navbar } from "~/components/layouts/Navbar";
import { Footer } from "~/components/layouts/footer/Footer";

interface LoaderData {
  districtData: InterfaceJasaByDistrict;
  serviceData: InterfaceJasaByDistrictService;
}
export const loader: LoaderFunction = async ({ params }) => {
  const { districtSlug, slug } = params;

  const districtData = dataJasaBangunRumahByDistrictBekasi.find(
    (district) => district.slug === districtSlug
  );

  const serviceData = districtData?.services.find(
    (service) => service.slug === slug
  );

  if (!serviceData) {
    throw new Response("Service not found", { status: 404 });
  }

  if (!districtData) {
    throw new Response("District Not Found", { status: 404 });
  }

  const selectedService = districtData.services.find(
    (service) => service.slug === slug
  );

  if (!selectedService) {
    throw new Response("Service Not Found", { status: 404 });
  }
  const breadcrumbItems = [
    { label: "Home", path: "/" },
    {
      label: "Jasa Bangun Rumah Di Bekasi",
      path: "/jasa-bangun-rumah-di-bekasi",
    },
    {
      label: districtData?.inaTitle,
      path: `/jasa-bangun-rumah-di-bekasi/${districtData?.slug}`,
    },
    {
      label: selectedService?.inaTitle,
      path: `/jasa-bangun-rumah-di-bekasi/${districtData?.slug}/${selectedService?.slug}`,
    },
  ];
  return json({
    selectedService,
    districtData,
    serviceData,
    breadcrumbItems,
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: BRAND,
      url: SITE_URL,
      logo: LOGO,
      sameAs: [PROFILE_FACEBOOK, PROFILE_INSTAGRAM, PROFILE_LINKEDIN],
    },
    // BUSINESSCHEMA
    businessSchema: {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      name: BRAND,
      image: LOGO,
      address: {
        "@type": "PostalAddress",
        streetAddress: ADDRESS,
        addressLocality: ADDRESS_CITY,
        postalCode: POSTAL_CODE,
        addressCountry: "ID",
      },
      telephone: WHATSAPP_NUM,
      priceRange: "Rp3.000 - Rp40.000",
      openingHoursSpecification: [
        {
          "@type": "OpeningHoursSpecification",
          dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
          opens: "09:00",
          closes: "18:00",
        },
      ],
    },
    // SERVICECHEMA
    serviceSchema: {
      "@context": "https://schema.org",
      "@type": "Service",
      name: SPAM_WORD_1,
      provider: {
        "@type": "Organization",
        name: AUTHOR,
        url: SITE_URL,
      },
    },
    // PRODUCTSCHEMA
    productSchema: {
      "@context": "https://schema.org",
      "@type": "Product",
      name: SPAM_WORD_1,
      description: DESCRIPTION_PROD_SCHEME,
      brand: {
        "@type": "Brand",
        name: AUTHOR,
      },
      sku: "GR-001",
      gtin: "123456789012",
      image: PROFILE_IMAGE,
      offers: {
        "@type": "Offer",
        price: "2000000",
        priceCurrency: "IDR",
        availability: "https://schema.org/InStock",
        url: SITE_URL,
        priceValidUntil: priceValidUntil,
        shippingDetails: {
          "@type": "OfferShippingDetails",
          shippingRate: {
            "@type": "MonetaryAmount",
            value: "10000",
            currency: "IDR",
          },
          shippingDestination: {
            "@type": "DefinedRegion",
            addressCountry: "ID",
          },
          deliveryTime: {
            "@type": "ShippingDeliveryTime",
            handlingTime: {
              "@type": "QuantitativeValue",
              minValue: 1,
              maxValue: 2,
              unitCode: "DAY",
            },
            transitTime: {
              "@type": "QuantitativeValue",
              minValue: 2,
              maxValue: 4,
              unitCode: "DAY",
            },
          },
        },
        hasMerchantReturnPolicy: {
          "@type": "MerchantReturnPolicy",
          applicableCountry: "ID",
          returnPolicyCategory:
            "https://schema.org/MerchantReturnFiniteReturnWindow",
          merchantReturnDays: 7,
          returnMethod: "https://schema.org/ReturnByMail",
          returnFees: "https://schema.org/FreeReturn",
        },
      },
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: "4.8",
        reviewCount: "2248",
      },
    },
  });
};
export const action = async ({ request }: ActionFunctionArgs) => {
  const formData = await request.formData();
  const { payload } = parseWithZod(formData, { schema: GlobalFormSchema });

  formData.append("Date", new Date().toISOString());
  formData.append("Whatsapp", String(payload?.whatsapp));

  return json({ msg: "success" });
};
export const meta: MetaFunction = ({ data }) => {
  const districtData = (data as LoaderData).districtData;
  const serviceData = (data as LoaderData).serviceData;
  const rawDescription = `⭐️ ${serviceData.inaTitle}  Gratis Konsultasi Tim Ahli  Whatsapp ${WHATSAPP_NUM} | ${SITE_URL}`;
  const title = titleWithOptionalBranding(
    "⭐️ Terbaik",
    serviceData.inaTitle,
    "arsinesia.com"
  );

  const description = truncatedDesc(rawDescription);
  const canonicalUrl = `${SITE_URL}/${SLUG_JASA_BANGUN_RUMAH_DI_BEKASI}/${districtData.slug}/${serviceData.slug}`;
  const keywords = [
    "Jasa Bangun Rumah di Bekasi",
    districtData.district,
    "Tukang Bangunan Terpercaya",
    "Jasa Renovasi Rumah",
    "Tukang Demak Berpengalaman",
    "Jasa Tukang Harian",
    "Tukang Cat Rumah",
    "Jasa Tukang Plafon",
    "Jasa Tukang Kanopi",
    "Jasa Tukang Listrik",
    "Jasa Tukang Baja Ringan",
    "Jasa Pasang Keramik",
    "Jasa Perbaikan Atap",
  ].join(", ");

  const rating = "5.0";
  const author = AUTHOR;
  const publisher = AUTHOR;

  return [
    { title: `${title}` },
    { name: "description", content: description },
    { name: "keywords", content: keywords },
    { property: "og:title", content: title },
    { property: "og:description", content: description },
    { property: "og:type", content: "website" },
    { property: "og:url", content: canonicalUrl },
    { property: "og:image", content: OG_IMAGE },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: title },
    { name: "twitter:description", content: description },
    { name: "twitter:image", content: TWITTER_IMAGE },
    { name: "viewport", content: "width=device-width, initial-scale=1.0" },
    { name: "rating", content: rating },
    { name: "author", content: author },
    { name: "publisher", content: publisher },
    { name: "datePublished", content: datePublished },
    { name: "dateModified", content: dateModified },
    { name: "robots", content: "index, follow" },
    {
      name: "robots",
      content: "max-snippet:-1, max-image-preview:large, max-video-preview:-1",
    },
    { property: "og:rating", content: rating },
    { property: "og:author", content: author },
    { property: "og:publisher", content: publisher },
    { property: "og:datePublished", content: datePublished },
    { property: "og:dateModified", content: dateModified },

    // ✅ Canonical tag (penting untuk SEO)
    { tagName: "link", rel: "canonical", href: canonicalUrl },

    // Optional: Structured data (schema.org)
    {
      property: "schema.org/Organization",
      content: JSON.stringify({
        "@context": "http://schema.org",
        "@type": "Organization",
        name: publisher,
        url: SITE_URL,
        logo: LOGO,
        sameAs: PROFILE_FACEBOOK,
      }),
    },
  ];
};

export default function Index() {
  const {
    selectedService,
    breadcrumbItems,
    structuredData,
    serviceSchema,
    businessSchema,
    productSchema,
  } = useLoaderData<{
    selectedService: InterfaceGlobalServices;
    districtData: any;
    dataJasaBangunRumahByDistrictBekasiByServices: InterfaceJasaByDistrictService[];
    dynamicUrls: { label: string; path: string }[];

    breadcrumbItems: InterfaceBreadcrumbItem[];
    structuredData: any;
    serviceSchema: any;
    businessSchema: any;
    productSchema: any;
  }>();

  const location = useLocation();
  const [currentPage, setCurrentPage] = useState<string | undefined>();
  useEffect(() => {
    setCurrentPage(`${SITE_URL}${location.pathname}`);
  }, [location.pathname]);

  function extractServiceTypeOnly(title: string) {
    return title
      .replace(/^Jasa Bangun Rumah\s+/, "")
      .replace(/\s+Di\s+.*$/, "")
      .trim();
  }

  if (!currentPage) return null;
  const toggleContact = () => {
    const phoneNumber = WHATSAPP_NUM;
    const message = encodeURIComponent(WHATSAPP_MESSAGE);
    const whatsappURL = `https://wa.me/${phoneNumber}?text=${message}`;
    window.open(whatsappURL, "_blank");
  };

  return (
    <>
      <Navbar />
      <GlobalMetaScripts
        structuredData={structuredData}
        serviceSchema={serviceSchema}
        businessSchema={businessSchema}
        productSchema={productSchema}
      />
      <GlobalBreadcrumb items={breadcrumbItems} />
      <GlobalCompoVideo />
      <section className="w-global mx-auto">
        <div className="pt-[3rem]">
          {/* <HomeSectionHero toggleContact={toggleContact} /> */}
          <section className="h-[100vh]">
            <GlobalSectionHero />
          </section>
        </div>
        <section className="py-[3rem] flex flex-col lg:flex-row gap-[1rem] lg:gap-[3rem] ">
          <div className="w-full lg:w-[50%]">
            <img
              className="w-full "
              src={selectedService.imagePrimary}
              alt=""
            />
          </div>
          <div className="w-full lg:w-[50%] flex flex-col gap-[0.5rem]">
            <h1 className="text-xl lg:text-2xl font-bold mb-4">
              {selectedService?.inaTitle ||
                "Jasa Bangun Rumah Profesional di Bekasi"}
            </h1>

            <GlobalRating rating={selectedService.rating} />
            <div className="flex flex-row gap-[0.5rem]">
              <p className="font-semibold">Vendor :</p>
              <Logo />
            </div>
            <div className="pt-[3rem]">
              <GlobalContactWhatsappFloating
                buttonText={`WA Spesialis ${extractServiceTypeOnly(
                  selectedService.inaTitle
                )}`}
              />
              <div className="pt-[3rem]">
                <Link
                  to={`/jasa-bangun-rumah-di-bekasi`}
                  className=" text-blue-600 underline"
                >
                  Lihat Jasa Bangun Rumah Di Bekasi Lainnya
                </Link>
              </div>
            </div>
          </div>
        </section>
      </section>
      <section className="pb-[10rem] m-global mx-auto relative overflow-hidden no-scrollbar">
        <ExploreJasaByCitySection />
      </section>
      <Footer />
    </>
  );
}
